import { isNil } from 'lodash';
import { Menu } from '@shared/components/Menu';
import { Text } from '@shared/components/Text';
import { View } from '@shared/components/View';
import { ControlOption } from '@shared/components/filter/types';
import { MBIconV2Name } from '@shared/components/image';
import { TextInput } from '@shared/components/textInput';
import { useWorkflowDispatch } from '@shared/scenes/workflows/context/context';
import { Divider } from '@shared/scenes/workflows/edit/components/optionsMenu/divider';
import { useWorkflowTemplate } from '@shared/scenes/workflows/hooks';
import { isTagComponent } from '@shared/scenes/workflows/utils';
import { grid } from '@shared/styles';
import { API } from '@shared/types/api';
import { PM60, useFeatureFlag } from '@shared/util/featureFlags';
import { i18n } from '@shared/util/i18n';

const MINIMUM_IN_SERIES_MIN = 1;
const MINIMUM_IN_SERIES_MAX = 100;

export function DateAutoExtendDefaultOptions({
  workflowTemplateId,
  component,
}: {
  workflowTemplateId: string;
  component: API.DateComponent;
}) {
  const { workflowTemplate } = useWorkflowTemplate(workflowTemplateId);
  const tagComponents = workflowTemplate?.components.filter(isTagComponent);
  const tagComponentOptions: ControlOption[] = (tagComponents ?? []).map(tagComponent => ({
    id: tagComponent.id,
    label: tagComponent.label ?? '',
    icon: tagComponent.icon as MBIconV2Name,
  }));
  const defaultTagComponentId = component.autoExtendDefault?.impliedFilterComponentId;
  const selectedTagComponentOption = tagComponentOptions?.find(
    to => to.id === defaultTagComponentId,
  );
  const dispatch = useWorkflowDispatch();
  const isPM60Enabled = useFeatureFlag(PM60);
  const minimumInSeries = component.autoExtendDefault?.minimumInSeries;
  const isMinimumInSeriesValid =
    isNil(minimumInSeries) ||
    (minimumInSeries >= MINIMUM_IN_SERIES_MIN && minimumInSeries <= MINIMUM_IN_SERIES_MAX);

  const handleMinimumInSeriesChange = (value: string) => {
    const numberValue = Number(value);
    if (isNaN(numberValue)) {
      return;
    }
    dispatch({
      type: 'updateComponent',
      args: {
        ...component,
        autoExtendDefault: {
          ...component.autoExtendDefault,
          minimumInSeries: numberValue || undefined,
        },
      } as API.DateComponent,
    });
  };

  const handleTagComponentOptionChange = (option: ControlOption) => {
    dispatch({
      type: 'updateComponent',
      args: {
        ...component,
        autoExtendDefault: {
          impliedFilterComponentId: defaultTagComponentId === option.id ? null : option.id,
          minimumInSeries: component.autoExtendDefault?.minimumInSeries,
        },
      } as API.DateComponent,
    });
  };

  if (!isPM60Enabled) {
    return null;
  }

  return (
    <>
      <View paddingHorizontal={3}>
        <View paddingVertical={1}>
          <Text size="body1" weight="semibold">
            {i18n('workflowTemplate.component.dateAndRepeat.defaultOptions.autoExtendTitle')}
          </Text>
          <View row marginTop={1}>
            <TextInput
              value={
                !isNil(minimumInSeries) ? String(minimumInSeries) : String(MINIMUM_IN_SERIES_MIN)
              }
              onChangeText={handleMinimumInSeriesChange}
              error={
                isMinimumInSeriesValid
                  ? undefined
                  : i18n(
                      'workflowTemplate.component.dateAndRepeat.defaultOptions.minimumInSeriesError',
                    )
              }
              placeholder="1-100"
              style={{ width: grid(10), marginBottom: grid(1) }}
            />
          </View>
          <Text size="body2">
            {i18n('workflowTemplate.component.dateAndRepeat.defaultOptions.minimumInSeries', {
              count: minimumInSeries ?? 0,
            })}
          </Text>
          <View paddingBottom={2} paddingTop={1}>
            <Menu>
              <Menu.Button
                label={
                  selectedTagComponentOption?.label ?? i18n('card.actions.groups.noneSelected')
                }
                icon={selectedTagComponentOption?.icon}
              />
              <Menu.Content>
                <Menu.Options
                  options={tagComponentOptions}
                  getItemKey={option => option.id}
                  getItemLabel={option => option.label}
                  getItemIcon={option => option.icon}
                  onSelect={handleTagComponentOptionChange}
                  isItemSelected={option => option.id === defaultTagComponentId}
                />
              </Menu.Content>
            </Menu>
          </View>
        </View>
      </View>
      <View paddingHorizontal={2}>
        <Divider />
      </View>
    </>
  );
}
